/** @typedef {import('./types').CrmDeal} CrmDeal */

export const WON_STAGE = 'Won'

/**
 * Column names for the board: the forecast columns (they carry counts and totals) when loaded,
 * otherwise the Lookups stages.
 * @param {{Columns?: {Stage: string}[]}|null} forecast
 * @param {{Name: string}[]} stages
 * @returns {string[]}
 */
export const columnNames = (forecast, stages) =>
    (forecast?.Columns?.length ? forecast.Columns.map(c => c.Stage) : (stages || []).map(s => s.Name))

/**
 * Show a pending (optimistic) move before the server confirms it.
 * @param {CrmDeal[]} deals
 * @param {Record<number, string>} pending dealId -> target stage
 * @returns {CrmDeal[]}
 */
export const applyPendingMoves = (deals, pending) =>
    deals.map(deal => (pending[deal.DealId] ? { ...deal, Stage: pending[deal.DealId] } : deal))

/**
 * Deals bucketed by stage in column order. Lost deals never appear on the board.
 * @param {CrmDeal[]} deals
 * @param {string[]} stageNames
 * @returns {Record<string, CrmDeal[]>}
 */
export const groupByStage = (deals, stageNames) => {
    /** @type {Record<string, CrmDeal[]>} */
    const groups = {}
    stageNames.forEach(name => {
        groups[name] = []
    })
    deals.forEach(deal => {
        if (deal.Status === 'lost') return
        if (groups[deal.Stage]) groups[deal.Stage].push(deal)
    })
    return groups
}

/** Only open deals can be dragged; won deals are read-only and lost deals are not on the board. */
export const canDrag = deal => deal.Status === 'open'

/**
 * What dropping a card should do. "Won" never goes to the Stage endpoint (400 USE_WON_ENDPOINT):
 * it opens the handoff dialog instead.
 * @param {CrmDeal} deal
 * @param {string} toStage
 * @returns {'noop'|'won'|'move'}
 */
export const resolveDrop = (deal, toStage) => {
    if (!canDrag(deal) || !toStage || toStage === deal.Stage) return 'noop'
    return toStage === WON_STAGE ? 'won' : 'move'
}

/** Stage names a deal can be created in or edited to (never Won). */
export const editableStages = stages => (stages || []).map(s => s.Name).filter(name => name !== WON_STAGE)

/** "42%" from a 0-1 probability. */
export const percent = probability => `${Math.round(Number(probability || 0) * 100)}%`
